import { useAccount, usePublicClient } from 'wagmi';
import { formatEther, parseAbiItem } from 'viem';

import { sankoWagerContract } from '@src/lib/consts';
import { useEffect, useState } from 'react';

export default function useWagerStats() {
  const { address } = useAccount();
  const publicClient = usePublicClient();

  const [wagerStats, setWagerStats] = useState({
    plays: 0,
    wins: 0,
    totalPayout: BigInt(0),
    totalWagered: BigInt(0),
  });

  useEffect(() => {
    const getData = async () => {
      if (!publicClient || !address) return;

      const games = await publicClient.getLogs({
        ...sankoWagerContract,
        event: parseAbiItem(
          'event GameJoined(uint256 indexed id, address indexed player)',
        ),
        args: { player: address },
        fromBlock: BigInt(0),
      });

      const gameIds = games.map((game) => game.args.id);
      const allWagers = await publicClient.getLogs({
        ...sankoWagerContract,
        event: parseAbiItem(
          'event GameStarted(uint256 indexed id, uint256 wagerAmount)',
        ),
        fromBlock: BigInt(0),
      });

      const playerWagers = allWagers.filter((wager) =>
        gameIds.includes(wager.args.id),
      );

      const totalWagered = playerWagers.reduce(
        (sum, wager) => (wager.args.wagerAmount ?? BigInt(0)) + sum,
        BigInt(0),
      );

      const gamesWon = await publicClient.getLogs({
        ...sankoWagerContract,
        event: parseAbiItem(
          'event GameDecided(uint256 indexed id, address indexed winner, uint256 payout)',
        ),
        args: { winner: address },
        fromBlock: BigInt(0),
      });

      const totalPayout = gamesWon.reduce(
        (sum, wager) => (wager.args.payout ?? BigInt(0)) + sum,
        BigInt(0),
      );

      setWagerStats({
        plays: games.length,
        wins: gamesWon.length,
        totalPayout,
        totalWagered,
      });
    };

    getData();
  }, [publicClient, address]);

  const { plays, wins, totalPayout, totalWagered } = wagerStats;
  const losses = plays - wins;
  const winLossRatio = losses > 0 ? (wins / (plays - wins)).toFixed(2) : '∞';
  return {
    plays,
    wins,
    losses,
    winLossRatio,
    wagered: Number(formatEther(totalWagered)).toFixed(2),
    won: Number(formatEther(totalPayout)).toFixed(2),
    profit: Number(Number(formatEther(totalPayout - totalWagered)).toFixed(2)),
  };
}
